import React, { useEffect, lazy, Suspense, useState, useCallback, useMemo } from 'react';
import { AxiosInstance } from 'axios';

import { Steps, useOnboardingStore } from './store/onboarding.store';
import { useLocalStorage } from '../../hooks/useLocalStorage';

import { OnboardingService } from '../../services/OnboardingService';
import { useApi } from '../../services/hooks/apiHook';

import { errorToast } from '../../utils/toasts';

import { errorMessageAdapter } from '../../adapters';

import { useOnboarding } from './Context/OnboardingContext';
import { useTranslation } from 'react-i18next';

// Lazy imports das páginas
const OnboardingObjectivesPage = lazy(() => import('./OnboardingObjectivesPage'));
const OnboardingDirectorsPage = lazy(() => import('./OnboardingDirectorsPage'));
const OnboardingShareholdersPage = lazy(() => import('./OnboardingShareholdersPage'));
const OnboardingMembersPage = lazy(() => import('./OnboardingMembersPage'));
const OnboardingSummaryPage = lazy(() => import('./OnboardingSummaryPage'));
const OnboardingAddonsPage = lazy(() => import('./OnboardingAddonsPage'));

const OnboardingContent = () => {
  const [loading, setLoading] = useState(true);
  const { dataOnboarding, updateDataOnboarding } = useOnboarding();
  const { step, setStep } = useOnboardingStore();
  const [token] = useLocalStorage('access-token', null);
  const api: AxiosInstance = useApi();
  const { i18n } = useTranslation();

  const headers = useMemo(() => ({
    'Content-Language': i18n.language,
    Authorization: `Bearer ${token}`,
  }), [i18n.language, token]);

  const getCorrectStep = (step: string, jurisdiction: string): Steps => {
    if (jurisdiction !== 'Bahamas' && step === 'shareholders') {
      return Steps.Members;
    }
    return step as Steps;
  };

  const getDataOnboarding = useCallback(async () => {
    try {
      const { success, data } = await OnboardingService.getDataOnboarding(api, { headers });

      if (success && data?.progress_step) {
        const newStep = getCorrectStep(data.progress_step, data.jurisdiction?.description);
        setStep(newStep);
        updateDataOnboarding(data);
      } else {
        setStep(Steps.Objectives);
        updateDataOnboarding(data);
        console.warn('Invalid onboarding step data');
      }
    } catch (error) {
      console.error('Error fetching onboarding data', error);
      errorToast(errorMessageAdapter(error));
      setStep(Steps.Objectives);
    } finally {
      setLoading(false);
    }
  }, [api, headers, setStep, updateDataOnboarding]);

  // Fetch onboarding on mount and whenever language changes (server returns translated fees/addons)
  useEffect(() => {
    if (token) {
      getDataOnboarding();
    }
  }, [token, i18n.language, getDataOnboarding]);

  useEffect(() => {
    if (dataOnboarding?.progress_step &&  dataOnboarding.jurisdiction?.description) {
      const resolvedStep = getCorrectStep(
        dataOnboarding.progress_step,
        dataOnboarding.jurisdiction?.description
      );
      setStep(resolvedStep);
    }
  }, [dataOnboarding?.progress_step, dataOnboarding?.jurisdiction?.description, setStep]);
  

  const redirectPage = useMemo<Record<Steps, JSX.Element | null>>(() => ({
    [Steps.Objectives]: <OnboardingObjectivesPage />,
    [Steps.Directors]: <OnboardingDirectorsPage />,
    [Steps.Shareholders]: <OnboardingShareholdersPage />,
    [Steps.Members]: <OnboardingMembersPage />,
    [Steps.Addons]: <OnboardingAddonsPage />,
    [Steps.Checkout]: <OnboardingSummaryPage />,
    [Steps.Summary]: <OnboardingSummaryPage />,
    [Steps.Jurisdiction]: null,
  }), []);

  return (
    <Suspense fallback={<div>Loading...</div>}>
      {loading
        ? <div className="my-auto pt-14 text-center">Loading Onboarding...</div>
        : redirectPage[step as Steps] ?? <OnboardingObjectivesPage />
      }
    </Suspense>
  );
};

const Onboarding = () => <OnboardingContent />;

export { Onboarding };
